/**
 * MarkerPriorityCalculator
 *
 * Calculates priority scores for markers based on screen position and size.
 * Lower scores = higher priority.
 *
 * Algorithm: priorityScore = (centerDistance * 0.7) - (size * 0.3)
 * - centerDistance: Distance from screen center (0 to √2)
 * - size: Marker scale/size
 */

import * as THREE from 'three';
import { HYSTERESIS_THRESHOLD } from '../config/performance.config.js';

export class MarkerPriorityCalculator {
  // Object pooling for Vector3 instances to reduce GC pressure
  static _vectorPool = {
    worldPosition: new THREE.Vector3(),
    screenPositionNDC: new THREE.Vector3(),
    cameraToMarker: new THREE.Vector3(),
    cameraForward: new THREE.Vector3(),
    worldScale: new THREE.Vector3(),
  };
  /**
   * Calculate edge proximity penalty - markers near screen edges get higher penalty
   * @param {THREE.Vector3} screenPosition - Normalized screen position [-1, 1]
   * @param {Object} viewport - Viewport dimensions {width, height}
   * @returns {number} Edge penalty in pixels (0-100)
   */
  static calculateEdgePenalty(screenPosition, viewport) {
    // Convert NDC [-1, 1] to [0, 1] for edge distance calculation
    const x = (screenPosition.x + 1) / 2;
    const y = (screenPosition.y + 1) / 2;

    // Calculate distance to nearest edge (0 at edge, 0.5 at center)
    // Clamp to ensure valid range [0, 0.5] even if marker is slightly off-screen
    const minEdgeDistance = Math.max(0, Math.min(0.5, Math.min(x, 1 - x, y, 1 - y)));

    // penalty: 0 (center) -> 1000 (edge) - massively increased to strictly enforce center priority
    // Markers near the edge will have a huge score (bad) and won't be selected
    const edgePenalty = minEdgeDistance < 0.4 ? (1 - minEdgeDistance / 0.4) * 1000 : 0;
    return edgePenalty;
  }

  /**
   * Calculate viewing angle penalty - markers viewed at steep angles get higher penalty
   * @param {THREE.Vector3} worldPosition - Marker world position
   * @param {THREE.Camera} camera - Three.js camera
   * @returns {number} Angle penalty in pixels (0-50)
   */
  static calculateAnglePenalty(worldPosition, camera) {
    // Calculate vector from camera to marker (using pooled vector)
    const cameraToMarker = this._vectorPool.cameraToMarker;
    cameraToMarker.subVectors(worldPosition, camera.position);
    cameraToMarker.normalize();

    // Camera forward direction (negative Z in Three.js, using pooled vector)
    const cameraForward = this._vectorPool.cameraForward;
    cameraForward.set(0, 0, -1);
    cameraForward.applyQuaternion(camera.quaternion);
    cameraForward.normalize();

    // Calculate angle between camera forward and marker direction
    const dotProduct = cameraForward.dot(cameraToMarker);
    const angle = Math.acos(Math.max(-1, Math.min(1, dotProduct))); // Clamp for numerical stability

    // Convert angle to degrees
    const angleDegrees = (angle * 180) / Math.PI;

    // Apply penalty for steep viewing angles (>30 degrees)
    // 0-30 degrees: no penalty, 30-60 degrees: linear penalty up to 50px
    const anglePenalty = angleDegrees > 30 ? ((angleDegrees - 30) / 30) * 50 : 0;

    return Math.min(anglePenalty, 50); // Cap at 50px
  }

  /**
   * T008: Calculate screen-space distance from viewport center
   * @param {THREE.Vector3} worldPosition - Marker world position
   * @param {THREE.Camera} camera - Three.js camera
   * @param {Object} viewport - Viewport dimensions {width, height}
   * @returns {number} Distance in pixels from viewport center
   */
  static calculateScreenDistance(worldPosition, camera, viewport) {
    // Project world position to normalized device coordinates [-1, 1]
    const screenPosition = worldPosition.clone();
    screenPosition.project(camera);

    // Convert NDC to pixel coordinates
    // NDC: [-1, 1] -> Pixels: [0, width/height]
    const pixelX = (screenPosition.x + 1) * 0.5 * viewport.width;
    const pixelY = (1 - screenPosition.y) * 0.5 * viewport.height; // Flip Y axis

    // Calculate viewport center in pixels
    const centerX = viewport.width * 0.5;
    const centerY = viewport.height * 0.5;

    // Calculate Euclidean distance from center in pixels
    const dx = pixelX - centerX;
    const dy = pixelY - centerY;
    const distancePixels = Math.sqrt(dx * dx + dy * dy);

    return distancePixels;
  }

  /**
   * Calculate priority score for a single marker
   * @param {Object} markerState - MarkerState object
   * @param {THREE.Camera} camera - Three.js camera
   * @param {Object} viewport - Viewport dimensions {width, height}
   * @returns {number} Priority score (lower = higher priority)
   */
  static calculatePriority(markerState, camera, viewport = null) {
    if (!markerState || markerState.trackingStatus !== 'tracking') {
      console.log(
        `🔍 [PRIORITY DEBUG] ${markerState?.markerId || 'unknown'}: Not tracking (status=${markerState?.trackingStatus})`
      );
      return Infinity; // Not tracking = lowest priority
    }

    if (!markerState.anchor || !markerState.anchor.group) {
      console.warn(
        `🔍 [PRIORITY DEBUG] ${markerState.markerId}: No anchor or group (anchor=${!!markerState.anchor}, group=${!!markerState.anchor?.group})`
      );
      return Infinity;
    }

    try {
      // Extract position from world matrix using pooled vector
      const worldPosition = this._vectorPool.worldPosition;
      markerState.anchor.group.getWorldPosition(worldPosition);

      // T008/T009: Calculate screen-space distance in pixels
      let screenDistance;
      if (viewport) {
        // Use pixel-based distance for accuracy (T008)
        screenDistance = this.calculateScreenDistance(worldPosition, camera, viewport);
      } else {
        // Fallback to NDC-based distance for backward compatibility (using pooled vector)
        const screenPosition = this._vectorPool.screenPositionNDC;
        screenPosition.copy(worldPosition);
        screenPosition.project(camera);
        screenDistance = Math.sqrt(
          screenPosition.x * screenPosition.x + screenPosition.y * screenPosition.y
        );
      }

      // T009: Calculate priority with detection confidence
      const confidence = markerState.confidence || 1.0; // MindAR detection confidence (0-1)
      const weight = markerState.markerDefinition?.priorityWeight || 1.0;

      // Calculate penalties for better center-focused selection (using pooled vector)
      const screenPositionNDC = this._vectorPool.screenPositionNDC;
      screenPositionNDC.copy(worldPosition);
      screenPositionNDC.project(camera);
      const edgePenalty = viewport ? this.calculateEdgePenalty(screenPositionNDC, viewport) : 0;
      const anglePenalty = this.calculateAnglePenalty(worldPosition, camera);

      // Priority score = screen distance (pixels) with confidence weighting + penalties
      // Lower distance = higher priority (lower score)
      // Higher confidence = slightly higher priority (small adjustment)
      const confidenceAdjustment = (1.0 - confidence) * 10; // Max 10px penalty for low confidence
      const priorityScore =
        (screenDistance + confidenceAdjustment + edgePenalty + anglePenalty) * weight;

      console.log(
        `🔍 [PRIORITY] ${markerState.markerId}: score=${priorityScore.toFixed(1)}px (dist=${screenDistance.toFixed(1)}px, edge=${edgePenalty.toFixed(1)}px, angle=${anglePenalty.toFixed(1)}px, conf=${confidence.toFixed(2)}, weight=${weight})`
      );

      return priorityScore;
    } catch (error) {
      console.error('Error calculating priority for marker:', markerState.markerId, error);
      return Infinity;
    }
  }

  /**
   * Calculate priority scores for all markers
   * @param {Array<Object>} markerStates - Array of MarkerState objects
   * @param {THREE.Camera} camera - Three.js camera
   * @param {Object} viewport - Viewport dimensions {width, height}
   * @returns {Map<string, number>} Map of markerId to priority score
   */
  static calculate(markerStates, camera, viewport = null) {
    const scores = new Map();

    markerStates.forEach((state) => {
      if (state.trackingStatus === 'tracking') {
        const score = this.calculatePriority(state, camera, viewport);
        scores.set(state.markerId, score);
      }
    });

    return scores;
  }

  /**
   * Select the best marker to be active
   * @param {Map} scores - Map of markerId -> score
   * @param {string} currentActiveId - Currently active marker ID
   * @returns {string|null} Best marker ID
   */
  static selectActive(scores, currentActiveId) {
    if (scores.size === 0) return null;

    // Sort by score (lower is better)
    const sorted = Array.from(scores.entries()).sort((a, b) => a[1] - b[1]);
    const [bestId, bestScore] = sorted[0];

    // If no current active marker, just pick the best one
    if (!currentActiveId) {
      return bestId;
    }

    // If best is same as current, keep it
    if (bestId === currentActiveId) {
      return currentActiveId;
    }

    // T045: "Tunnel Vision" - Strict switching requirement
    // To switch FROM an active marker TO a new marker, the new marker
    // must be strictly within the center "sweet spot" (Tunnel Vision).
    // This prevents unwanted switching when a new marker enters the edge.

    // We need to check the raw distance score component of the best marker
    // Since we don't have the raw components here, we infer from the score logic
    // or better, we check if the score is significantly low (meaning it's very good)

    // "Exit Strategy": Check if current marker is "bad" (at edge)
    // If current score is high (> 500), it means it has a high edge penalty.
    // In this case, we should be eager to switch away from it.
    const currentScore = scores.get(currentActiveId) || Infinity;
    const isCurrentBad = currentScore > 500;

    // Dynamic Tunnel Vision Threshold
    // If current is good: Strict (250) - Only switch if new is PERFECTLY centered
    // If current is bad: Relaxed (600) - Switch if new is DECENT (doesn't have to be perfect)
    const effectiveThreshold = isCurrentBad ? 600 : 250;

    if (bestScore > effectiveThreshold) {
      // New marker is best, BUT it's not good enough to warrant a switch.
      // Stick with current marker to prevent "edge switching".
      return currentActiveId;
    }

    // Standard hysteresis check
    // If current is NOT bad, apply hysteresis.
    // If current IS bad (at edge), IGNORE hysteresis (switch immediately if better).
    if (
      !isCurrentBad &&
      currentScore !== Infinity &&
      currentScore - bestScore < HYSTERESIS_THRESHOLD
    ) {
      return currentActiveId;
    }

    return bestId;
  }

  /**
   * Get priority ranking of all markers
   * @param {Map<string, number>} scores - Priority scores map
   * @returns {Array<{markerId: string, score: number, rank: number}>}
   */
  static getRanking(scores) {
    if (!scores || scores.size === 0) {
      return [];
    }

    // Convert to array and sort by score (ascending)
    const sorted = Array.from(scores.entries())
      .map(([markerId, score]) => ({ markerId, score }))
      .sort((a, b) => a.score - b.score);

    // Add rank (1-based)
    return sorted.map((item, index) => ({
      ...item,
      rank: index + 1,
    }));
  }

  /**
   * T010: Check if marker should be switched based on hysteresis
   * @param {string} candidateMarkerId - Candidate marker ID
   * @param {number} candidateScore - Candidate priority score (pixels)
   * @param {string|null} currentMarkerId - Current active marker ID
   * @param {number|null} currentScore - Current priority score (pixels)
   * @param {number} hysteresis - Hysteresis threshold in pixels (default: HYSTERESIS_THRESHOLD = 50px)
   * @returns {boolean} True if should switch
   */
  static shouldSwitch(
    candidateMarkerId,
    candidateScore,
    currentMarkerId,
    currentScore,
    hysteresis = HYSTERESIS_THRESHOLD
  ) {
    // If no current marker, switch to candidate
    if (!currentMarkerId) {
      return true;
    }

    // If candidate is same as current, no switch
    if (candidateMarkerId === currentMarkerId) {
      return false;
    }

    // If current score not available, switch
    if (currentScore === null || currentScore === undefined || currentScore === Infinity) {
      return true;
    }

    // T010: Switch only if candidate is significantly closer (hysteresis in pixels)
    // currentScore (higher = farther) - candidateScore (lower = closer) > threshold
    // This means candidate must be at least hysteresis pixels closer to switch
    const scoreDifference = currentScore - candidateScore;
    const shouldSwitch = scoreDifference > hysteresis;

    if (shouldSwitch) {
      console.log(
        `🔄 [HYSTERESIS] Switch: ${currentMarkerId} → ${candidateMarkerId} (diff=${scoreDifference.toFixed(1)}px > ${hysteresis}px)`
      );
    } else {
      console.log(
        `🔒 [HYSTERESIS] Keep: ${currentMarkerId} (diff=${scoreDifference.toFixed(1)}px ≤ ${hysteresis}px)`
      );
    }

    return shouldSwitch;
  }
}
